// watch/useWatchProgress.ts — your place in a video (1.27.60, concept ⋯ "Mark as unwatched").
// Signed-in only. Resumes where you left off (unless the link says where to start, or you
// already watched it), saves your place every 15 seconds of playback and when you leave the
// page, and marks the video watched when it ends. The server counts 90% as watched too.
import { useCallback, useEffect, useRef, useState } from 'react';
import { videoFeedService } from '@/core/services/videoFeedService';
import { usePostStore } from '@/core/stores/postStore';
import type { Post, WatchProgress } from '@/core/models/post';

/** Nothing is saved before this — opening a video by mistake leaves no trace. */
export const MIN_SAVE_SEC = 5;
export const SAVE_EVERY_MS = 15_000;
/** Too close to either end to be worth resuming. */
const RESUME_MARGIN_SEC = 10;

/** Where to pick up, or undefined to start from the beginning. */
export function resumePoint(
  progress: WatchProgress | null | undefined,
  durationSec: number,
): number | undefined {
  if (!progress || progress.watched) return undefined;
  const sec = progress.positionMs / 1000;
  return sec >= RESUME_MARGIN_SEC && sec < durationSec - RESUME_MARGIN_SEC ? sec : undefined;
}

export function useWatchProgress(post: Post, signedIn: boolean) {
  const video = post.video!;
  const durationSec = video.durationMs / 1000;
  // Decided once, on arrival: a save while you watch must not move the player.
  const [resumeFrom] = useState(() =>
    signedIn ? resumePoint(post.watchProgress, durationSec) : undefined,
  );
  const last = useRef({ savedAt: 0, savedPos: -1, pos: 0 });

  const save = useCallback(
    (sec: number, watched = false) => {
      if (!signedIn) return;
      last.current.savedAt = Date.now();
      last.current.savedPos = sec;
      void videoFeedService
        .saveProgress(video.id, sec * 1000, watched)
        .then((progress) => usePostStore.getState().patchPost(post.id, { watchProgress: progress }))
        .catch(() => {});
    },
    [signedIn, video.id, post.id],
  );

  const onTime = useCallback(
    (sec: number) => {
      last.current.pos = sec;
      if (!signedIn || sec < MIN_SAVE_SEC) return;
      if (Date.now() - last.current.savedAt < SAVE_EVERY_MS) return;
      if (Math.abs(sec - last.current.savedPos) < 1) return;
      save(sec);
    },
    [signedIn, save],
  );

  const onEnded = useCallback(() => save(durationSec, true), [save, durationSec]);

  // Leaving the page keeps your place.
  useEffect(() => {
    const state = last.current;
    return () => {
      if (state.pos >= MIN_SAVE_SEC && Math.abs(state.pos - state.savedPos) >= 1) {
        save(state.pos);
      }
    };
  }, [save]);

  return { resumeFrom, onTime, onEnded };
}
